import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  RefreshControl,
} from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { RootStackParamList, Transaction } from '../types';
import { transactionsService } from '../services/transactions';
import { useDialog } from '../contexts';

type Props = NativeStackScreenProps<RootStackParamList, 'AdminTransactions'>;

const STATUS_COLOR: Record<string, { bg: string; text: string }> = {
  pending:        { bg: '#FFF3E0', text: '#E65100' },
  buyer_confirmed:{ bg: '#E3F2FD', text: '#1565C0' },
  paid:           { bg: '#E8F5E9', text: '#2E7D32' },
  disputed:       { bg: '#FFEBEE', text: '#C62828' },
};

function daysAgo(dateStr: string, t: TFunction): string {
  const ms = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(ms / 86400000);
  if (days === 0) return t('screens.adminTransactions.lessThanDay');
  return t('screens.adminTransactions.daysAgo', { count: days });
}

function displayName(profile: any): string {
  if (!profile) return '–';
  const name = `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim();
  return name || profile.email || '–';
}

export const AdminTransactionsScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const dialog = useDialog();

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [processing, setProcessing] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await transactionsService.getAllOpenTransactions();
      setTransactions(data);
    } catch (err: any) {
      dialog.alert(t('common.errors.error'), err.message, 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t, dialog]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (
    tx: Transaction,
    action: 'cancelled' | 'pending'
  ) => {
    const label = action === 'cancelled'
      ? t('screens.adminTransactions.cancelConfirm')
      : t('screens.adminTransactions.resetConfirm');

    const confirmed = await dialog.confirm(label, '', { variant: 'danger' });
    if (!confirmed) return;

    setProcessing(tx.id);
    try {
      await transactionsService.adminUpdateTransactionStatus(tx.id, action);
      setTransactions(prev => prev.filter(t => t.id !== tx.id));
    } catch (err: any) {
      dialog.alert(t('common.errors.error'), err.message, 'error');
    } finally {
      setProcessing(null);
    }
  };

  const renderItem = ({ item: tx }: { item: Transaction }) => {
    const item = tx.item;
    const buyer = tx.buyer;
    const seller = tx.seller;
    const colors = STATUS_COLOR[tx.status] ?? { bg: '#F5F5F5', text: '#333' };
    const isProcessing = processing === tx.id;
    const isPaid = tx.status === 'paid';
    const canReset = !isPaid && tx.status !== 'pending';

    return (
      <View style={styles.card}>
        {/* Header row */}
        <View style={styles.cardHeader}>
          <View style={[styles.statusBadge, { backgroundColor: colors.bg }]}>
            <Text style={[styles.statusText, { color: colors.text }]}>
              {t(`screens.adminTransactions.status.${tx.status}`, { defaultValue: tx.status })}
            </Text>
          </View>
          <Text style={styles.age}>{daysAgo(tx.created_at, t)}</Text>
        </View>

        {/* Item */}
        <Text style={styles.itemTitle} numberOfLines={1}>
          {item?.title ?? t('screens.adminTransactions.itemDeleted')}
          {item?.club?.name ? ` · ${item.club.name}` : ''}
        </Text>

        {/* Parties */}
        <View style={styles.parties}>
          <Text style={styles.party}>
            🛒 {displayName(buyer)}
          </Text>
          <Text style={styles.partySep}>→</Text>
          <Text style={styles.party}>
            🏷 {displayName(seller)}
          </Text>
        </View>

        {/* Amount */}
        <Text style={styles.amount}>€{tx.total_amount.toFixed(2)}</Text>

        {/* Actions */}
        <View style={styles.actions}>
          {isProcessing ? (
            <ActivityIndicator color="#007AFF" />
          ) : isPaid ? (
            <Text style={styles.refundNote}>
              {t('screens.adminTransactions.useRefund')}
            </Text>
          ) : (
            <>
              <TouchableOpacity
                style={[styles.btn, styles.btnDanger]}
                onPress={() => handleAction(tx, 'cancelled')}
              >
                <Text style={styles.btnText}>
                  {t('screens.adminTransactions.cancel')}
                </Text>
              </TouchableOpacity>
              {canReset && (
                <TouchableOpacity
                  style={[styles.btn, styles.btnSecondary]}
                  onPress={() => handleAction(tx, 'pending')}
                >
                  <Text style={styles.btnText}>
                    {t('screens.adminTransactions.reset')}
                  </Text>
                </TouchableOpacity>
              )}
            </>
          )}
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={[styles.header, Platform.OS === 'web' && styles.headerWeb]}>
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('AdminDashboard')}
          >
            <Ionicons name="arrow-back" size={24} color="#007AFF" />
            <Text style={styles.backText}>{t('common.buttons.back')}</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('screens.adminTransactions.title')}</Text>
          <Text style={styles.headerSubtitle}>
            {t('screens.adminTransactions.subtitle', { count: transactions.length })}
          </Text>
        </View>
      </View>

      {loading ? (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color="#007AFF" />
        </View>
      ) : (
        <FlatList
          data={transactions}
          renderItem={renderItem}
          keyExtractor={tx => tx.id}
          contentContainerStyle={[styles.list, Platform.OS === 'web' && styles.listWeb]}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(); }} />}
          ListEmptyComponent={
            <View style={styles.empty}>
              <Text style={styles.emptyIcon}>✅</Text>
              <Text style={styles.emptyText}>{t('screens.adminTransactions.empty')}</Text>
            </View>
          }
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  header: {
    padding: 16,
    paddingTop: Platform.OS === 'web' ? 16 : 50,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  headerWeb: { alignItems: 'center' },
  headerContent: { width: '100%', maxWidth: 1200 },
  backButton: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  backText: { fontSize: 16, color: '#007AFF', marginLeft: 4 },
  headerTitle: { fontSize: 24, fontWeight: '600', color: '#333' },
  headerSubtitle: { fontSize: 14, color: '#888', marginTop: 2 },
  list: { padding: 16 },
  listWeb: { alignSelf: 'center', width: '100%', maxWidth: 800 },
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  statusBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12 },
  statusText: { fontSize: 12, fontWeight: '700' },
  age: { fontSize: 12, color: '#999' },
  itemTitle: { fontSize: 16, fontWeight: '600', color: '#222', marginBottom: 6 },
  parties: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 },
  party: { fontSize: 13, color: '#555', flex: 1 },
  partySep: { fontSize: 13, color: '#bbb' },
  amount: { fontSize: 18, fontWeight: '700', color: '#1B3A2D', marginBottom: 12 },
  actions: { flexDirection: 'row', gap: 8 },
  btn: {
    flex: 1,
    paddingVertical: 8,
    borderRadius: 8,
    alignItems: 'center',
  },
  btnDanger: { backgroundColor: '#FF3B30' },
  btnSecondary: { backgroundColor: '#8E8E93' },
  btnText: { color: '#fff', fontSize: 14, fontWeight: '600' },
  refundNote: { flex: 1, fontSize: 13, color: '#888', fontStyle: 'italic', textAlign: 'center', paddingVertical: 8 },
  empty: { alignItems: 'center', paddingTop: 60 },
  emptyIcon: { fontSize: 40, marginBottom: 12 },
  emptyText: { fontSize: 16, color: '#888' },
});
